import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from './AppHeader';
import { useResponsive } from '../hooks/useResponsive';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { useMyJoinApplications, MyJoinApplicationRow } from '../hooks/useMyJoinApplications';
import JoinApplicationThreadModal from './JoinApplicationThreadModal';
import { theme, alpha } from '../theme';

const STATUS_COPY: Record<string, { label: string; color: string; bg: string }> = {
  PENDING: { label: 'Pendiente', color: theme.colors.warning, bg: alpha('warning', '20') },
  ACCEPTED: { label: 'Aceptada', color: theme.colors.success, bg: alpha('success', '20') },
  REJECTED: { label: 'No aceptada', color: theme.colors.danger, bg: alpha('danger', '20') },
};

const MyJoinApplicationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const flags = useFeatureFlags() as Record<string, any>;
  const publicMatchDiscover = Boolean(flags.PUBLIC_MATCH_DISCOVER);
  const { rows, loading, error, cancel, refresh } = useMyJoinApplications();
  const [thread, setThread] = useState<MyJoinApplicationRow | null>(null);

  if (!flags.loading && !publicMatchDiscover) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: theme.colors.background,
          padding: isMobile ? '80px 16px 20px' : '20px 40px',
        }}
      >
        <AppHeader title="Mis postulaciones" showBackButton />
        <div
          style={{
            maxWidth: 480,
            margin: '40px auto',
            padding: 32,
            background: theme.colors.surface,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: 16,
            textAlign: 'center',
            color: theme.colors.textSecondary,
          }}
        >
          Función desactivada.
        </div>
      </div>
    );
  }

  const handleCancel = async (row: MyJoinApplicationRow) => {
    if (!row.match) return;
    const ok = window.confirm('¿Cancelar esta postulación?');
    if (!ok) return;
    await cancel(row.match.id, row.id);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: theme.colors.background,
        padding: isMobile ? '80px 16px 20px' : '20px 40px',
      }}
    >
      <AppHeader title="Mis postulaciones" showBackButton />

      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <p style={{ color: theme.colors.textSecondary, fontSize: 14, marginTop: 0 }}>
          El estado se actualiza cuando el organizador acepta o rechaza tu postulación.
        </p>

        {error && (
          <div
            style={{
              padding: '12px 16px',
              background: alpha('danger', '20'),
              border: `1px solid ${theme.colors.danger}`,
              borderRadius: 10,
              color: theme.colors.danger,
              marginBottom: 16,
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: theme.colors.textSecondary }}>
            Cargando postulaciones…
          </div>
        ) : rows.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: 40,
              background: theme.colors.surface,
              border: `1px dashed ${theme.colors.border}`,
              borderRadius: 16,
              color: theme.colors.textSecondary,
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>📨</div>
            <div style={{ fontWeight: 700, color: theme.colors.text, marginBottom: 4 }}>
              Todavía no te postulaste a ningún partido
            </div>
            <button
              type="button"
              onClick={() => navigate('/discover')}
              style={{
                marginTop: 12,
                padding: '10px 16px',
                background: theme.colors.primary,
                color: theme.colors.background,
                border: 'none',
                borderRadius: 10,
                fontWeight: 800,
                cursor: 'pointer',
                fontFamily: theme.fonts.heading,
              }}
            >
              Buscar partidos
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {rows.map((row) => {
              const status = STATUS_COPY[row.status] || {
                label: row.status,
                color: theme.colors.textSecondary,
                bg: theme.colors.background,
              };
              return (
                <div
                  key={row.id}
                  style={{
                    background: theme.colors.surface,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: 14,
                    padding: 16,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          color: theme.colors.text,
                          fontWeight: 700,
                          fontSize: 16,
                          fontFamily: theme.fonts.heading,
                        }}
                      >
                        {row.match?.location || 'Partido'}
                      </div>
                      <div style={{ color: theme.colors.textSecondary, fontSize: 13, marginTop: 2 }}>
                        {row.match?.date} · {row.match?.time}
                        {row.match?.price != null ? ` · $${row.match.price}` : ''}
                      </div>
                    </div>
                    <span
                      style={{
                        background: status.bg,
                        color: status.color,
                        padding: '4px 10px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        height: 'fit-content',
                      }}
                    >
                      {status.label}
                    </span>
                  </div>

                  <div
                    style={{
                      marginTop: 10,
                      padding: 10,
                      background: theme.colors.background,
                      borderRadius: 8,
                      color: theme.colors.text,
                      fontSize: 13,
                      fontStyle: 'italic',
                    }}
                  >
                    “{row.message}”
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    {row.match && (
                      <button
                        type="button"
                        onClick={() => navigate(`/match/${row.match!.id}`)}
                        style={{
                          padding: '8px 12px',
                          background: theme.colors.background,
                          color: theme.colors.text,
                          border: `1px solid ${theme.colors.border}`,
                          borderRadius: 8,
                          fontWeight: 600,
                          fontSize: 13,
                          cursor: 'pointer',
                        }}
                      >
                        Ver partido
                      </button>
                    )}
                    {row.status === 'PENDING' && (
                      <>
                        <button
                          type="button"
                          onClick={() => setThread(row)}
                          style={{
                            padding: '8px 12px',
                            background: 'transparent',
                            color: theme.colors.primary,
                            border: `1px solid ${theme.colors.primary}`,
                            borderRadius: 8,
                            fontWeight: 600,
                            fontSize: 13,
                            cursor: 'pointer',
                          }}
                        >
                          💬 Mensajes
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCancel(row)}
                          style={{
                            padding: '8px 12px',
                            background: 'transparent',
                            color: theme.colors.danger,
                            border: `1px solid ${theme.colors.danger}`,
                            borderRadius: 8,
                            fontWeight: 600,
                            fontSize: 13,
                            cursor: 'pointer',
                            marginLeft: 'auto',
                          }}
                        >
                          Cancelar
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {thread && thread.match && (
        <JoinApplicationThreadModal
          isOpen={Boolean(thread)}
          matchId={thread.match.id}
          applicationId={thread.id}
          applicationStatus={thread.status}
          otherPartyLabel={thread.match.creator?.name || 'Organizador'}
          initialApplicationMessage={thread.message}
          chatRole="applicant"
          onClose={() => {
            setThread(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
};

export default MyJoinApplicationsPage;
